import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "styled-components";

import { useScrollGroupItem } from "../ScrollGroup";
import type { StickyGridContextType } from "./context";
import { useStickyGrid } from "./context";

export function StickyGridHeader({
  children,
  nonScrollChildren,
  stickOffset,
  onStickyStateChange,
}: {
  children: ReactNode;
  nonScrollChildren?: (context: {
    grid: StickyGridContextType;
    isStuck: boolean;
  }) => ReactNode;
  stickOffset?: number;
  onStickyStateChange?: (isStuck: boolean) => void;
}) {
  const grid = useStickyGrid();
  const { templateColumns, gap } = grid;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useScrollGroupItem({ refObject: contentRef });
  const [elementHeight, setElementHeight] = useState<number | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    onStickyStateChange?.(isStuck);
  }, [isStuck, onStickyStateChange]);

  // We need this so we can update the value of element.offsetHeight, which allows StickyGridHeader to set isStuck at the correct scroll position no matter where the observer is painted on the layout
  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const updateHeight = () => setElementHeight(element.offsetHeight);

    const resizeObserver = new ResizeObserver(updateHeight);

    resizeObserver.observe(element);

    // Set initial height immediately
    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [wrapperRef]);

  const THRESHOLD = useMemo(() => {
    if (!elementHeight || !stickOffset) return 1;
    const value = (elementHeight - stickOffset) / elementHeight;
    const INTERSECTION_RATIO_BUFFER = 1.00001; // `value` is very accurate, but is consistently lesser than entry.intersectionRatio. This constant slightly increases the value of `THRESHOLD` so that `THRESHOLD` can be greater than entry.intersectionRatio

    return Math.max(0, Math.min(1, value * INTERSECTION_RATIO_BUFFER)); // We need to return a value between 0 and 1
  }, [elementHeight, stickOffset]);

  useEffect(() => {
    if (!wrapperRef.current) {
      setIsStuck(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setIsStuck(entry.intersectionRatio < THRESHOLD);
      },
      { threshold: THRESHOLD },
    );

    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [THRESHOLD]);

  return (
    <HeaderWrap ref={wrapperRef} $stickOffset={stickOffset}>
      <StyledStickyHeader
        ref={contentRef}
        $templateColumns={templateColumns}
        $gap={gap}
      >
        {children}
      </StyledStickyHeader>
      {nonScrollChildren?.({ grid, isStuck })}
    </HeaderWrap>
  );
}

const HeaderWrap = styled.div<{
  $stickOffset?: number;
}>`
  width: 100%;
  position: sticky;
  top: -${({ $stickOffset }) => $stickOffset ?? 0}px;
  z-index: 2;
  transition: box-shadow 0.25s;
`;

const StyledStickyHeader = styled.div<{
  $templateColumns: string;
  $gap?: string;
}>`
  display: grid;

  gap: ${({ $gap }) => $gap ?? 0};
  grid-template-columns: ${({ $templateColumns }) => $templateColumns};
  width: 100%;
  position: sticky;
  z-index: 2;

  transition: box-shadow 0.25s;

  overflow: auto;
  overscroll-behavior-x: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
